/**
 * Hex, RGB, and HSV helpers, plus the colours already stored on a theme.
 * The colour picker uses these. Nothing here reads the database.
 */

const SITE_KEYS = [
    'background', 'text', 'sideMenuBackground', 'sideMenuText', 'headerBackground', 'headerText', 'caption',
    'viewerBackground', 'viewerControls', 'viewerControlsBackground',
];
const PAGE_KEYS = ['themeBackground', 'themeText', 'themeSideMenuBackground', 'themeSideMenuText', 'themeHeaderBackground', 'themeHeaderText', 'themeCaption'];
const BLOCK_KEYS = ['color', 'hoverColor', 'drawerColor', 'drawerHoverColor'];
const VIDEO_IDS = ['video-play', 'video-scrubber', 'video-time', 'video-caption'];
const VIDEO_KEYS = ['color', 'trackColor', 'fillColor'];
const MEDIA_IDS = ['media-title'];
const MEDIA_KEYS = ['color', 'background'];

/**
 * @param {unknown} value
 * @returns {string|null} lowercase #rrggbb
 */
export function normalizeHex(value) {
    if (typeof value !== 'string') return null;
    const raw = value.trim();
    const match = raw.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (!match) return null;
    const hex = match[1].toLowerCase();
    if (hex.length === 3) {
        return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
    }
    return `#${hex}`;
}

const NAMED_COLORS = {
    black: '#000000',
    white: '#ffffff',
    red: '#ff0000',
    green: '#008000',
    lime: '#00ff00',
    blue: '#0000ff',
    yellow: '#ffff00',
    cyan: '#00ffff',
    aqua: '#00ffff',
    magenta: '#ff00ff',
    fuchsia: '#ff00ff',
    gray: '#808080',
    grey: '#808080',
    silver: '#c0c0c0',
    maroon: '#800000',
    olive: '#808000',
    purple: '#800080',
    teal: '#008080',
    navy: '#000080',
    orange: '#ffa500',
    pink: '#ffc0cb',
    brown: '#a52a2a',
    gold: '#ffd700',
    indigo: '#4b0082',
    violet: '#ee82ee',
    transparent: '#000000',
    rebeccapurple: '#663399',
};

function clampChannel(n, max) {
    if (!Number.isFinite(n)) return null;
    return Math.max(0, Math.min(max, n));
}

function partsOf(inner) {
    return String(inner).trim().split(/[,/\s]+/).map((part) => part.trim()).filter(Boolean);
}

function amount(token, scale) {
    if (typeof token !== 'string' || token === '') return null;
    const percent = token.endsWith('%');
    const n = parseFloat(token);
    if (!Number.isFinite(n)) return null;
    return percent ? (n / 100) * scale : n;
}

function hslToRgb(h, s, l) {
    const hue = ((h % 360) + 360) % 360;
    const sat = Math.max(0, Math.min(1, s));
    const light = Math.max(0, Math.min(1, l));
    const c = (1 - Math.abs((2 * light) - 1)) * sat;
    const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
    const m = light - (c / 2);
    let rp;
    let gp;
    let bp;
    if (hue < 60) { rp = c; gp = x; bp = 0; }
    else if (hue < 120) { rp = x; gp = c; bp = 0; }
    else if (hue < 180) { rp = 0; gp = c; bp = x; }
    else if (hue < 240) { rp = 0; gp = x; bp = c; }
    else if (hue < 300) { rp = x; gp = 0; bp = c; }
    else { rp = c; gp = 0; bp = x; }
    return {
        r: Math.round((rp + m) * 255),
        g: Math.round((gp + m) * 255),
        b: Math.round((bp + m) * 255),
    };
}

function hexBody(body) {
    const hex = body.toLowerCase();
    if (hex.length === 3 || hex.length === 6) return normalizeHex(`#${hex}`);
    if (hex.length === 4) return normalizeHex(`#${hex.slice(0, 3)}`);
    if (hex.length === 8) return normalizeHex(`#${hex.slice(0, 6)}`);
    return null;
}

/**
 * Turn a pasted colour into #rrggbb.
 * Accepts hex (with or without #, including alpha digits), rgb/rgba, hsl/hsla,
 * hsv/hsva, a bare "r, g, b" triple, and common CSS colour names.
 * @param {unknown} value
 * @returns {string|null}
 */
export function parseColor(value) {
    if (typeof value !== 'string') return null;
    const raw = value.trim().replace(/;$/, '');
    if (!raw) return null;

    const hexish = raw.match(/^#?([0-9a-f]{3,8})$/i);
    if (hexish) return hexBody(hexish[1]);

    const fn = raw.match(/^(rgba?|hsla?|hsva?)\(\s*([^)]*)\)?$/i);
    if (fn) {
        const kind = fn[1].toLowerCase();
        const args = partsOf(fn[2]);
        if (args.length < 3) return null;
        if (kind.startsWith('rgb')) {
            const scale = args[0].endsWith('%') ? 255 : 1;
            const r = clampChannel(amount(args[0], 255) / (args[0].endsWith('%') ? 1 : scale), 255);
            const g = clampChannel(amount(args[1], 255), 255);
            const b = clampChannel(amount(args[2], 255), 255);
            if (r == null || g == null || b == null) return null;
            return rgbToHex(r, g, b);
        }
        const h = parseFloat(args[0]);
        const s = amount(args[1], 1);
        const third = amount(args[2], 1);
        if (!Number.isFinite(h) || s == null || third == null) return null;
        const rgb = kind.startsWith('hsl')
            ? hslToRgb(h, s > 1 ? s / 100 : s, third > 1 ? third / 100 : third)
            : hsvToRgb(h, s > 1 ? s / 100 : s, third > 1 ? third / 100 : third);
        return rgbToHex(rgb.r, rgb.g, rgb.b);
    }

    const triple = partsOf(raw);
    if (triple.length === 3 && triple.every((part) => /^-?[\d.]+%?$/.test(part))) {
        const r = clampChannel(amount(triple[0], 255), 255);
        const g = clampChannel(amount(triple[1], 255), 255);
        const b = clampChannel(amount(triple[2], 255), 255);
        if (r == null || g == null || b == null) return null;
        return rgbToHex(r, g, b);
    }

    const named = NAMED_COLORS[raw.toLowerCase()];
    if (named) return named;

    return browserColor(raw);
}

/** Browser-only fallback for lab, oklch, and the rest of the CSS colour names. */
function browserColor(value) {
    if (typeof document === 'undefined') return null;
    const probe = document.createElement('span');
    probe.style.color = value;
    if (!probe.style.color) return null;
    document.body.appendChild(probe);
    const computed = getComputedStyle(probe).color;
    probe.remove();
    const match = computed.match(/rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/i);
    if (!match) return null;
    return rgbToHex(Number(match[1]), Number(match[2]), Number(match[3]));
}

/**
 * @param {number} r
 * @param {number} g
 * @param {number} b
 * @returns {string}
 */
export function rgbToHex(r, g, b) {
    const channel = (n) => {
        const clamped = Math.max(0, Math.min(255, Math.round(n)));
        return clamped.toString(16).padStart(2, '0');
    };
    return `#${channel(r)}${channel(g)}${channel(b)}`;
}

/**
 * @param {string} hex
 * @returns {{ r: number, g: number, b: number }|null}
 */
export function hexToRgb(hex) {
    const normal = normalizeHex(hex);
    if (!normal) return null;
    return {
        r: parseInt(normal.slice(1, 3), 16),
        g: parseInt(normal.slice(3, 5), 16),
        b: parseInt(normal.slice(5, 7), 16),
    };
}

/**
 * A CSS colour with alpha. Opacity is 0–1; missing or invalid colour is null.
 * @param {unknown} value
 * @param {unknown} [opacity=1]
 * @returns {string|null}
 */
export function colorWithAlpha(value, opacity = 1) {
    const hex = parseColor(value) || normalizeHex(value);
    if (!hex) return null;
    const rgb = hexToRgb(hex);
    if (!rgb) return null;
    const alpha = Number(opacity);
    const a = Number.isFinite(alpha) ? Math.max(0, Math.min(1, alpha)) : 1;
    if (a >= 1) return hex;
    if (a <= 0) return 'transparent';
    const rounded = Math.round(a * 1000) / 1000;
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${rounded})`;
}

/**
 * @param {number} r
 * @param {number} g
 * @param {number} b
 * @returns {{ h: number, s: number, v: number }} H 0-360, S and V 0-1
 */
export function rgbToHsv(r, g, b) {
    const rn = r / 255;
    const gn = g / 255;
    const bn = b / 255;
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const delta = max - min;
    let h = 0;
    if (delta !== 0) {
        if (max === rn) h = ((gn - bn) / delta) % 6;
        else if (max === gn) h = (bn - rn) / delta + 2;
        else h = (rn - gn) / delta + 4;
        h *= 60;
        if (h < 0) h += 360;
    }
    return {
        h,
        s: max === 0 ? 0 : delta / max,
        v: max,
    };
}

/**
 * @param {number} h 0-360
 * @param {number} s 0-1
 * @param {number} v 0-1
 * @returns {{ r: number, g: number, b: number }}
 */
export function hsvToRgb(h, s, v) {
    const hue = ((h % 360) + 360) % 360;
    const sat = Math.max(0, Math.min(1, s));
    const val = Math.max(0, Math.min(1, v));
    const c = val * sat;
    const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
    const m = val - c;
    let rp;
    let gp;
    let bp;
    if (hue < 60) { rp = c; gp = x; bp = 0; }
    else if (hue < 120) { rp = x; gp = c; bp = 0; }
    else if (hue < 180) { rp = 0; gp = c; bp = x; }
    else if (hue < 240) { rp = 0; gp = x; bp = c; }
    else if (hue < 300) { rp = x; gp = 0; bp = c; }
    else { rp = c; gp = 0; bp = x; }
    return {
        r: Math.round((rp + m) * 255),
        g: Math.round((gp + m) * 255),
        b: Math.round((bp + m) * 255),
    };
}

function pushColor(list, seen, value) {
    const hex = normalizeHex(value);
    if (!hex || seen.has(hex)) return;
    seen.add(hex);
    list.push(hex);
}

function pushSite(list, seen, colors) {
    if (!colors || typeof colors !== 'object') return;
    SITE_KEYS.forEach((key) => pushColor(list, seen, colors[key]));
}

/**
 * Unique colours already stored on the theme, and optionally the open page.
 * @param {Object} [theme]
 * @param {Object} [page]
 * @returns {string[]}
 */
export function collectThemeColors(theme, page) {
    const list = [];
    const seen = new Set();

    pushSite(list, seen, theme?.colors);

    ['phone', 'tablet'].forEach((layer) => {
        const map = theme?.overrides?.[layer];
        if (!map || typeof map !== 'object') return;
        pushSite(list, seen, map.colors);
        SITE_KEYS.forEach((key) => pushColor(list, seen, map[`colors.${key}`]));
    });

    (theme?.header?.blocks || []).forEach((block) => {
        const props = block?.props;
        if (!props) return;
        BLOCK_KEYS.forEach((key) => pushColor(list, seen, props[key]));
    });

    const modules = theme?.header?.modules || {};
    [...VIDEO_IDS, ...MEDIA_IDS].forEach((id) => {
        const entry = modules[id];
        if (!entry || typeof entry !== 'object') return;
        ['desktop', 'phone', 'tablet'].forEach((layer) => {
            const box = entry[layer];
            if (!box) return;
            const keys = MEDIA_IDS.includes(id) ? MEDIA_KEYS : VIDEO_KEYS;
            keys.forEach((key) => pushColor(list, seen, box[key]));
        });
    });

    if (page) PAGE_KEYS.forEach((key) => pushColor(list, seen, page[key]));
    if (page?.viewerLook && typeof page.viewerLook === 'object') {
        Object.values(page.viewerLook)
            .filter((value) => typeof value === 'string')
            .forEach((value) => pushColor(list, seen, value));
    }

    return list;
}
